import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  DollarSign, TrendingUp, Users, Package, Settings, Brain,
  BarChart3, AlertTriangle, Send, Loader2, ChevronRight,
  Calendar, Building2, Shield, Activity, Zap, Target,
  ArrowUpRight, ArrowDownRight, X
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useActiveRole } from '@/hooks/useActiveRole';
import ReactMarkdown from 'react-markdown';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface IntelligenceCategory {
  id: string;
  label: string;
  icon: React.ElementType;
  color: string;
  queries: string[];
}

const CATEGORIES: IntelligenceCategory[] = [
  {
    id: 'finance',
    label: 'Finance Intelligence',
    icon: DollarSign,
    color: 'text-success',
    queries: [
      'Revenue trends this quarter',
      'Cash flow risk assessment',
      'AR aging analysis',
      'Budget deviation report',
      'Expense anomaly detection',
    ],
  },
  {
    id: 'sales',
    label: 'Sales Intelligence',
    icon: TrendingUp,
    color: 'text-primary',
    queries: [
      'Pipeline risk analysis',
      'Lead quality assessment',
      'Win probability forecast',
      'Revenue forecast next 90 days',
      'Stalled deals detection',
    ],
  },
  {
    id: 'hr',
    label: 'HR Intelligence',
    icon: Users,
    color: 'text-info',
    queries: [
      'Attendance pattern analysis',
      'Overtime cost breakdown',
      'Leave trend analysis',
      'Team utilization report',
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory Intelligence',
    icon: Package,
    color: 'text-warning',
    queries: [
      'Slow-moving stock report',
      'Stockout risk prediction',
      'Overstock analysis',
      'Expiry risk assessment',
    ],
  },
  {
    id: 'operations',
    label: 'Operational Intelligence',
    icon: Settings,
    color: 'text-muted-foreground',
    queries: [
      'Workflow bottleneck analysis',
      'SLA breach prediction',
      'Task overload detection',
      'Production efficiency metrics',
    ],
  },
];

export default function IntelligenceWorkspace({ onClose }: { onClose: () => void }) {
  const { user, role, hasRole } = useAuth();
  const activeRole = useActiveRole();
  const [selectedCategory, setSelectedCategory] = useState<string>('finance');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('this_month');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const activeCategory = CATEGORIES.find(c => c.id === selectedCategory) || CATEGORIES[0];

  const sendQuery = async (text?: string) => {
    const msgText = text || input.trim();
    if (!msgText || loading) return;

    const userMsg: Message = { role: 'user', content: msgText };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: {
          query: `[${activeCategory.label}] ${msgText}. Provide structured analysis with key metrics, risk indicators, and actionable recommendations. Use tables and bullet points for clarity.`,
          conversationHistory: messages.slice(-6),
        },
      });

      if (error) throw error;

      if (data?.error) {
        if (data.error.includes('429') || data.error.includes('Rate limit')) {
          setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ Rate limit reached. Please try again in a moment.' }]);
        } else {
          throw new Error(data.error);
        }
        return;
      }

      setMessages(prev => [...prev, { role: 'assistant', content: data?.response || 'No response' }]);
    } catch (err: any) {
      console.error('Intelligence error:', err);
      setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ Analysis failed. Please retry.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="flex h-full">
        {/* LEFT PANEL - Intelligence Navigator */}
        <div className="w-64 border-r border-border bg-muted/30 flex flex-col">
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" />
              <div>
                <h2 className="font-semibold text-sm">Clicknify Intelligence</h2>
                <p className="text-2xs text-muted-foreground">Enterprise AI Engine</p>
              </div>
            </div>
          </div>

          <ScrollArea className="flex-1 p-3">
            <div className="space-y-1">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => { setSelectedCategory(cat.id); setMessages([]); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left text-sm transition-colors ${
                    selectedCategory === cat.id
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <cat.icon className={`h-4 w-4 ${selectedCategory === cat.id ? 'text-primary' : cat.color}`} />
                  <span className="truncate">{cat.label}</span>
                </button>
              ))}
            </div>

            <Separator className="my-4" />

            <div className="px-1">
              <p className="text-2xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Quick Queries</p>
              <div className="space-y-1">
                {activeCategory.queries.map((q) => (
                  <button
                    key={q}
                    onClick={() => sendQuery(q)}
                    className="w-full text-left text-xs px-3 py-2 rounded-md bg-background border border-border hover:border-primary/30 hover:bg-primary/5 transition-colors text-muted-foreground hover:text-foreground"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </ScrollArea>
        </div>

        {/* CENTER PANEL - AI Analysis Output */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="h-14 border-b border-border flex items-center justify-between px-6 bg-card">
            <div className="flex items-center gap-3">
              <activeCategory.icon className={`h-5 w-5 ${activeCategory.color}`} />
              <div>
                <h3 className="font-semibold text-sm">{activeCategory.label}</h3>
                <p className="text-2xs text-muted-foreground">AI-powered analysis engine</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-2xs">
                <Shield className="h-3 w-3 mr-1" />
                {activeRole || 'viewer'} scope
              </Badge>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Analysis Output */}
          <ScrollArea className="flex-1 p-6" ref={scrollRef}>
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center max-w-md mx-auto">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <Brain className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{activeCategory.label}</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Select a query from the left panel or type a custom analysis request below.
                </p>
                <div className="grid grid-cols-2 gap-3 w-full">
                  {activeCategory.queries.slice(0, 4).map((q) => (
                    <button
                      key={q}
                      onClick={() => sendQuery(q)}
                      className="text-left text-xs p-3 rounded-lg border border-border bg-card hover:border-primary/30 hover:shadow-sm transition-all text-muted-foreground hover:text-foreground"
                    >
                      <Zap className="h-3 w-3 text-primary mb-1" />
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-6 max-w-3xl mx-auto">
                {messages.map((msg, i) => (
                  <div key={i}>
                    {msg.role === 'user' ? (
                      <div className="flex items-start gap-3 mb-4">
                        <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Target className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Analysis Request</p>
                          <p className="text-sm font-medium">{msg.content}</p>
                        </div>
                      </div>
                    ) : (
                      <Card className="border-border">
                        <CardHeader className="pb-3 pt-4 px-5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Brain className="h-4 w-4 text-primary" />
                              <CardTitle className="text-sm">Intelligence Analysis</CardTitle>
                            </div>
                            <Badge variant="outline" className="text-2xs">
                              <Activity className="h-3 w-3 mr-1" />
                              Live Data
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="px-5 pb-4">
                          <div className="prose prose-sm dark:prose-invert max-w-none
                            [&>*:first-child]:mt-0 [&>*:last-child]:mb-0
                            [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5
                            [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_h4]:text-xs
                            [&_table]:text-xs [&_table]:w-full
                            [&_th]:bg-muted/50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold
                            [&_td]:px-3 [&_td]:py-2 [&_td]:border-b [&_td]:border-border
                            [&_strong]:text-foreground">
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                ))}
                {loading && (
                  <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="p-4 flex items-center gap-3">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      <div>
                        <p className="text-sm font-medium">Analyzing organizational data...</p>
                        <p className="text-2xs text-muted-foreground">Scanning {activeCategory.label.toLowerCase()} metrics</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Input Bar */}
          <div className="border-t border-border p-4 bg-card">
            <form onSubmit={(e) => { e.preventDefault(); sendQuery(); }} className="flex gap-3 max-w-3xl mx-auto">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={`Ask about ${activeCategory.label.toLowerCase()}...`}
                className="flex-1 text-sm"
                disabled={loading}
              />
              <Button type="submit" disabled={loading || !input.trim()} size="sm">
                <Send className="h-4 w-4 mr-2" />
                Analyze
              </Button>
            </form>
          </div>
        </div>

        {/* RIGHT PANEL - Context Controls */}
        <div className="w-56 border-l border-border bg-muted/30 flex flex-col">
          <div className="p-4 border-b border-border">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Data Scope</h3>
          </div>

          <div className="p-4 space-y-4">
            <div>
              <label className="text-2xs font-medium text-muted-foreground uppercase tracking-wider">Period</label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="mt-1.5 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="this_week">This Week</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="this_quarter">This Quarter</SelectItem>
                  <SelectItem value="this_year">This Year</SelectItem>
                  <SelectItem value="last_90_days">Last 90 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-2xs font-medium text-muted-foreground uppercase tracking-wider">Role Access</label>
              <div className="mt-1.5 px-3 py-2 rounded-md bg-background border border-border text-xs">
                <div className="flex items-center gap-2">
                  <Shield className="h-3 w-3 text-primary" />
                  <span className="capitalize">{activeRole || 'viewer'}</span>
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <label className="text-2xs font-medium text-muted-foreground uppercase tracking-wider">Data Sources</label>
              <div className="mt-2 space-y-1.5">
                {[
                  { label: 'Invoices', icon: DollarSign },
                  { label: 'CRM Leads', icon: TrendingUp },
                  { label: 'HR Records', icon: Users },
                  { label: 'Inventory', icon: Package },
                ].map((src) => (
                  <div key={src.label} className="flex items-center gap-2 text-2xs text-muted-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-success" />
                    <src.icon className="h-3 w-3" />
                    <span>{src.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            <div>
              <label className="text-2xs font-medium text-muted-foreground uppercase tracking-wider">Confidence</label>
              <div className="mt-1.5 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-success rounded-full w-4/5" />
                </div>
                <span className="text-2xs font-medium">High</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
